/**
 * Inaugural 90-Day Protocol details panel (PDP).
 *
 * The OFFER ITSELF is chosen in the pack selector — this panel never adds a
 * second selectable choice for the same bottles. It explains the live
 * campaign: what it costs, the eligible-prepaid estimate, the Money-Back
 * Promise summary, and when the offer ends.
 *
 * Every amount is derived from the campaign row and the shared pricing
 * resolver; the payable amount is re-verified server-side at payment.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ShieldCheck, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatInr, prepaidEstimate } from "@/lib/promo/effectivePacks";
import {
  formatEndDateLong,
  formatEndDateShort,
  msUntilExpiry,
  shouldShowCountdown,
  type PromoCampaign,
} from "@/lib/promo/inaugural";

function useCountdown(campaign: PromoCampaign, enabled: boolean): string | null {
  const [left, setLeft] = useState(() => msUntilExpiry(campaign));
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setLeft(msUntilExpiry(campaign)), 1000);
    return () => clearInterval(id);
  }, [campaign, enabled]);
  if (!enabled || left <= 0) return null;
  const h = Math.floor(left / 3_600_000);
  const m = Math.floor((left % 3_600_000) / 60_000);
  const s = Math.floor((left % 60_000) / 1000);
  return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s left`;
}

type Props = {
  campaign: PromoCampaign;
  /** Regular single-bottle selling price in rupees (from the shared resolver). */
  unitPrice: number;
  /** Whether the shopper currently has the protocol quantity selected. */
  selected: boolean;
  className?: string;
};

export default function InauguralProtocolCard({
  campaign,
  unitPrice,
  selected,
  className,
}: Props) {
  const showCountdown = shouldShowCountdown(campaign);
  const countdown = useCountdown(campaign, showCountdown);

  const offer = campaign.offerPricePaise / 100;
  const regular = unitPrice * campaign.qualifyingQty;
  const savings = Math.max(0, regular - offer);
  const endsShort = useMemo(() => formatEndDateShort(campaign), [campaign]);
  const endsLong = useMemo(() => formatEndDateLong(campaign), [campaign]);

  return (
    <div
      className={cn(
        "rounded-2xl border p-4 transition-colors sm:p-5",
        selected ? "border-brand bg-brand-soft/60" : "border-border bg-card",
        className,
      )}
    >
      <span className="inline-flex items-center gap-1.5 rounded-full bg-brand px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white">
        <Sparkles className="h-3 w-3" aria-hidden="true" />
        {campaign.qualifyingQty * 30}-day inaugural offer
      </span>
      <h3 className="mt-2 font-display text-lg font-semibold leading-tight text-brand-deep">
        Get {campaign.qualifyingQty} bottles. Pay for {campaign.qualifyingQty - 1}.
      </h3>

      <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="text-2xl font-semibold tracking-tight text-brand-deep">
          {formatInr(offer)}
        </span>
        <span className="text-sm text-muted-foreground line-through">
          {formatInr(regular)}
        </span>
        {savings > 0 && (
          <span className="text-sm font-medium text-brand">
            Save {formatInr(savings)}
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {formatInr(regular)} is {campaign.qualifyingQty} bottles at the regular
        selling price — not MRP.
      </p>
      <p className="mt-1 text-xs font-medium text-brand-deep">
        Extra 5% off with eligible prepaid payment ·{" "}
        {formatInr(prepaidEstimate(offer))} estimated
      </p>
      <p className="mt-1 text-[11px] text-muted-foreground">
        The prepaid benefit is applied once, by the payment gateway. Cash on
        delivery pays {formatInr(offer)}.
      </p>

      <p className="mt-2 text-xs text-muted-foreground">
        Offer ends {endsShort} · <span className="sr-only">{endsLong}</span>
        <span aria-hidden="true">{endsLong}</span>
      </p>
      {countdown && (
        <p className="mt-1 text-xs font-medium text-brand">{countdown}</p>
      )}

      <div className="mt-3 border-t border-border/60 pt-3 text-xs text-muted-foreground">
        <p className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
          <span>
            <span className="font-medium text-foreground">
              60-Day TruWe Money-Back Promise.
            </span>{" "}
            Give the protocol a fair try. If you&rsquo;re not satisfied with your
            experience within 60 days, you&rsquo;re covered under our
            satisfaction policy.
          </span>
        </p>
        <ul className="mt-2 space-y-1 pl-6">
          <li>· First purchase of this product only, one claim per household.</li>
          <li>· Request within 60 days of delivery.</li>
          <li>
            · Use it consistently for at least 30 days first — this minimum does
            not apply if you stop because of intolerance, a reaction or a safety
            concern.
          </li>
          <li>
            · For a {campaign.qualifyingQty}-bottle protocol, at least one
            unopened bottle must be returned.
          </li>
          <li>· Shipping and return charges are not refunded.</li>
        </ul>
        <Link
          to="/policies/money-back-promise"
          className="mt-2 inline-block underline underline-offset-2 hover:text-foreground"
        >
          Read the full promise
        </Link>
      </div>
    </div>
  );
}
